import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { BasicTooltip, Icon, InputWithIcon, SelectMenu, TableLoader } from '@livable/saas-react-components';
import { Button, Container, MenuItem, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import RangeDatePicker from 'src/components/general/RangeDatePicker';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { useStore } from 'src/stores';
import { useDelay } from 'src/utils/hooks';
import useStyles from './styles';
const ActivityLogTable = observer(_ref => {
  let propertyId = _ref.propertyId;
  const store = useStore();
  const classes = useStyles();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    activityTypes = _useState2[0],
    setActivityTypes = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isLoading = _useState4[0],
    setIsLoading = _useState4[1];
  const _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    logsList = _useState6[0],
    setLogsList = _useState6[1];
  const _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    search = _useState8[0],
    setSearch = _useState8[1];
  const _useState9 = useState({
      fromDate: '',
      toDate: ''
    }),
    _useState0 = _slicedToArray(_useState9, 2),
    dates = _useState0[0],
    setDates = _useState0[1];
  const _useState1 = useState('all'),
    _useState10 = _slicedToArray(_useState1, 2),
    mate = _useState10[0],
    setMate = _useState10[1];
  const _useState11 = useState({
      page: 0,
      pageSize: 25
    }),
    _useState12 = _slicedToArray(_useState11, 2),
    paginationModel = _useState12[0],
    setPaginationModel = _useState12[1];
  const _useState13 = useState('all'),
    _useState14 = _slicedToArray(_useState13, 2),
    selectedType = _useState14[0],
    setSelectedType = _useState14[1];
  const _useState15 = useState([{
      field: 'timestamp',
      sort: 'desc'
    }]),
    _useState16 = _slicedToArray(_useState15, 2),
    sortModel = _useState16[0],
    setSortModel = _useState16[1];
  const _useState17 = useState([]),
    _useState18 = _slicedToArray(_useState17, 2),
    teammates = _useState18[0],
    setTeammates = _useState18[1];
  const fetchLogs = () => {
    var _sortModel$, _sortModel$2;
    if (!propertyId) return;
    setIsLoading(true);
    const getActivityLogs = async data => {
      const resp = await store.activity.getActivityLogsAsync.run(data);
      return resp;
    };
    getActivityLogs({
      activityType: selectedType === 'all' ? '' : selectedType,
      companyId: localStorage.getItem(USER_SETTINGS_KEYS.company),
      from: moment(dates.fromDate).utc(false).toISOString(),
      propertyId,
      search,
      sortBy: (_sortModel$ = sortModel[0]) === null || _sortModel$ === void 0 ? void 0 : _sortModel$.field,
      sortOrder: (_sortModel$2 = sortModel[0]) === null || _sortModel$2 === void 0 ? void 0 : _sortModel$2.sort,
      to: moment(dates.toDate).utc(false).toISOString(),
      teammates: mate === 'all' ? '' : mate,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize
    }).then(async resp => {
      setIsLoading(false);
      if (resp && resp.response && resp.response.items) {
        const currentLogs = [];
        resp.response.items.map(item => {
          currentLogs.push({
            activityOverview: item.activityOverview,
            activityType: item.activityType,
            appliedToEntityType: item.appliedToEntityType,
            initiatedByUserFullname: item.initiatedByUserFullname,
            timestamp: item.timestamp
          });
        });
        setLogsList(currentLogs);
      }
    });
  };
  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates, mate, paginationModel, propertyId, selectedType, sortModel]);
  useEffect(() => {
    setIsLoading(true);
    const getCompanyTeamMembers = async data => {
      const resp = await store.teamMembers.getCompanyTeamMembersAsync.run(data);
      return resp;
    };
    const getActivityTypes = async data => {
      const resp = await store.activity.getActivityTypesAsync.run(data);
      return resp;
    };
    Promise.all([getActivityTypes(), getCompanyTeamMembers({
      companyId: localStorage.getItem(USER_SETTINGS_KEYS.company)
    })]).then(async resp => {
      if (!resp[1]) {
        return;
      }
      setActivityTypes(resp[0].response);
      setTeammates(resp[1].response);
      setIsLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useDelay(() => fetchLogs(), [search], 1000);
  const handleDateChange = d => {
    if (d.startDate && d.endDate) {
      setDates({
        fromDate: moment(d.startDate).startOf('day'),
        toDate: moment(d.endDate).endOf('day')
      });
    }
  };
  const handleMateChange = event => {
    if (event.target.value) setMate(event.target.value);
  };
  const handlePaginationModel = data => setPaginationModel(data);
  const handleSortModel = data => {
    if (data.length > 1) {
      setSortModel(data);
    }
  };
  const handleTypeChange = event => {
    if (event.target.value) setSelectedType(event.target.value);
  };
  const columns = [{
    field: 'date',
    width: 220,
    headerName: t('general.date'),
    align: 'right',
    renderCell: params => /*#__PURE__*/React.createElement("div", {
      className: classes.cellContent
    }, moment(params.row.timestamp).format('MMM D, YYYY hh:mm'))
  }, {
    field: 'initiatedByUserFullname',
    width: 180,
    headerName: t('general.who'),
    align: 'right',
    renderCell: params => /*#__PURE__*/React.createElement("div", {
      className: classes.cellContent
    }, params.row.initiatedByUserFullname)
  }, {
    field: 'activityType',
    width: 220,
    headerName: t('residencies_page.activity_type'),
    align: 'right',
    renderCell: params => /*#__PURE__*/React.createElement("div", {
      className: classes.overflowCellWrapper
    }, /*#__PURE__*/React.createElement(BasicTooltip, {
      title: params.row.activityType,
      placement: "top",
      hideOnMobile: true
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.cellContent
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1",
      className: classes.overflowCell
    }, params.row.activityType))))
  }, {
    field: 'appliedToEntityType',
    width: 150,
    headerName: t('residencies_page.applied_to'),
    align: 'right',
    renderCell: params => /*#__PURE__*/React.createElement("div", {
      className: classes.cellContent
    }, params.row.appliedToEntityType)
  }, {
    field: 'activityOverview',
    width: 410,
    headerName: t('general.overview'),
    align: 'right',
    renderCell: params => /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.activityOverview, " ").concat(classes.cellContent)
    }, params.row.activityOverview)
  }, {
    field: 'details',
    width: 100,
    headerName: t('general.details'),
    align: 'left',
    headerAlign: 'right',
    filterable: false,
    sortable: false,
    hideable: false,
    renderCell: params => /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.cellContent, " ").concat(classes.rightAlign)
    }, /*#__PURE__*/React.createElement(BasicTooltip, {
      title: params.row.activityOverview,
      placement: "top"
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "actionIcon",
      size: "small",
      className: classes.trigger,
      'custom-attribute': '435-activity-log-table'
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "question"
    }))))
  }];
  return /*#__PURE__*/React.createElement(Container, {
    className: classes.pageContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.searchBar
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.searchField
  }, /*#__PURE__*/React.createElement(InputWithIcon, {
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "search"
    })
  }, /*#__PURE__*/React.createElement(TextField, {
    id: "charges-search",
    label: t('general.search'),
    variant: "outlined",
    fullWidth: true,
    onChange: e => setSearch(e.target.value),
    value: search
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.filterSelector
  }, /*#__PURE__*/React.createElement(SelectMenu, {
    disabled: isLoading,
    id: "mate-selector",
    label: t('general.who'),
    labelId: "mate-selector-label",
    onChange: handleMateChange,
    value: mate,
    'custom-attribute': '436-activity-log-table'
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: "all"
  }, t('residencies_page.all_teammates')), teammates.map(teammate => /*#__PURE__*/React.createElement(MenuItem, {
    value: teammate.id,
    key: teammate.id
  }, teammate.firstName, " ", teammate.lastName)))), /*#__PURE__*/React.createElement("div", {
    className: classes.filterSelector
  }, /*#__PURE__*/React.createElement(SelectMenu, {
    disabled: isLoading,
    id: "changes-type-selector",
    label: t('residencies_page.changes_type'),
    labelId: "changes-type-selector-label",
    onChange: handleTypeChange,
    value: selectedType,
    'custom-attribute': '437-activity-log-table'
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: "all"
  }, t('residencies_page.all_types')), activityTypes.map(type => /*#__PURE__*/React.createElement(MenuItem, {
    value: type,
    key: type
  }, type)))), /*#__PURE__*/React.createElement("div", {
    className: classes.datePicker
  }, /*#__PURE__*/React.createElement(RangeDatePicker, {
    fromDate: dates.fromDate,
    handleDateChange: d => handleDateChange(d),
    noValidate: true,
    toDate: dates.toDate,
    'custom-attribute': '438-activity-log-table'
  }))), isLoading ? /*#__PURE__*/React.createElement(TableLoader, null) : /*#__PURE__*/React.createElement(DataGrid, {
    autoHeight: true,
    columnHeaderHeight: 77,
    columns: columns,
    disableRowSelectionOnClick: true,
    getRowId: () => uuid(),
    hideFooterSelectedRowCount: true,
    onSortModelChange: model => handleSortModel(model),
    onPaginationModelChange: model => handlePaginationModel(model),
    pageSize: 10,
    paginationModel: paginationModel,
    rowHeight: 77,
    rowsPerPageOptions: [10],
    rows: logsList,
    slots: {
      BaseButton: props => /*#__PURE__*/React.createElement(Button, _extends({}, props, {
        variant: "actionButton",
        'custom-attribute': '439-activity-log-table'
      })),
      BaseTextField: props => /*#__PURE__*/React.createElement(TextField, _extends({}, props, {
        variant: "outlined"
      }))
    },
    slotsProps: {
      filterPanel: {
        filterFormProps: {
          columnInputProps: {
            variant: 'outlined'
          },
          operatorInputProps: {
            variant: 'outlined'
          }
        }
      }
    },
    sortModel: sortModel
  }));
});
export default ActivityLogTable;